import type { Editor, NodeEntry, Path } from 'slate';
import { Node, Text } from 'slate';

import {
  getCachedFooter,
  getCachedHeader,
  getFooterCacheKey,
  getHeaderCacheKey,
  getOrCreateCachedRMNode,
  setCachedFooter,
  setCachedHeader,
} from './cache';
import { assertNeverSafely, isEvenNum } from '../../util';
import type {
  AlineContentBlock,
  CMPageHeadersAndFooters,
  FooterElement,
  HeaderElement,
  HeaderFooterProps,
  RenderModelContentBlock,
  RenderModelFooter,
  RenderModelHeader,
} from '../internal';
import { Queries } from '../internal';

/**
 * Get from cache or create new header and footer for a given page number
 */
export function getOrCreateRMHeaderFooterForPage(
  editor: Editor,
  cmHeadersAndFooters: CMPageHeadersAndFooters,
  activeHeaderFooterProps: HeaderFooterProps,
  pageNum: number
): { rMFooter: RenderModelFooter; rMHeader: RenderModelHeader } {
  const rMHeader = getOrCreateRMHeader(
    editor,
    cmHeadersAndFooters,
    activeHeaderFooterProps,
    pageNum
  );
  const rMFooter = getOrCreateRMFooter(
    editor,
    cmHeadersAndFooters,
    activeHeaderFooterProps,
    pageNum
  );
  return { rMFooter, rMHeader };
}

export function getOrCreateRMHeader(
  editor: Editor,
  cmHeadersAndFooters: CMPageHeadersAndFooters,
  activeHeaderFooterProps: HeaderFooterProps,
  pageNum: number
): RenderModelHeader {
  let headerEntry: NodeEntry<HeaderElement>;

  if (activeHeaderFooterProps.diffFirstPage && pageNum === 1) {
    headerEntry = cmHeadersAndFooters.firstPageHeader;
  } else if (activeHeaderFooterProps.evenOdd && isEvenNum(pageNum)) {
    headerEntry = cmHeadersAndFooters.evenHeader;
  } else {
    headerEntry = cmHeadersAndFooters.defaultHeader;
  }

  const headerType = headerEntry[0].type;

  const cacheKey = getHeaderCacheKey(headerType, pageNum);
  const cachedHeader = getCachedHeader(cacheKey, headerType);

  if (cachedHeader) {
    return cachedHeader;
  }

  const header = buildHeaderOrFooter(headerEntry, editor, pageNum);
  setCachedHeader(cacheKey, headerType, header);

  return header;
}

export function getOrCreateRMFooter(
  editor: Editor,
  cmHeadersAndFooters: CMPageHeadersAndFooters,
  activeHeaderFooterProps: HeaderFooterProps,
  pageNum: number
): RenderModelFooter {
  let footerEntry: NodeEntry<FooterElement>;

  if (activeHeaderFooterProps.diffFirstPage && pageNum === 1) {
    footerEntry = cmHeadersAndFooters.firstPageFooter;
  } else if (activeHeaderFooterProps.evenOdd && isEvenNum(pageNum)) {
    footerEntry = cmHeadersAndFooters.evenFooter;
  } else {
    footerEntry = cmHeadersAndFooters.defaultFooter;
  }

  const footerType = footerEntry[0].type;

  const cacheKey = getFooterCacheKey(footerType, pageNum);
  const cachedFooter = getCachedFooter(cacheKey, footerType);

  if (cachedFooter) {
    return cachedFooter;
  }

  const footer = buildHeaderOrFooter(footerEntry, editor, pageNum);
  setCachedFooter(cacheKey, footerType, footer);

  return footer;
}

export interface ReduceCMNodeState {
  // Path to the parent node in the *content model*.
  cMParentPath: Path;
  editor: Editor;
  heightPx: number;
  pageNum: number;
  // The rMNode we are building.
  rMNode: RenderModelHeader | RenderModelFooter;
  // Path to the parent node in the rMFooter we are building.
  rMParentPath: Path;
}

export function reduceCMChild(
  state: ReduceCMNodeState,
  cMChild: AlineContentBlock,
  cMChildIndex: number
): ReduceCMNodeState {
  const { cMParentPath, editor, heightPx, pageNum, rMNode, rMParentPath } = state;
  const containerWidthPx = Queries.activePageContentWidth(editor, 'px');
  const parentInRMNode = Node.get(rMNode, rMParentPath);
  const cMChildPath = cMParentPath.concat(cMChildIndex);
  if (Text.isText(parentInRMNode)) return state;

  switch (cMChild.type) {
    case 'paragraph': {
      const rMPara = getOrCreateCachedRMNode(editor, {
        cMNodeEntry: [cMChild, cMChildPath],
        containerWidthPx,
        isInHeaderFooter: true,
        pageNum,
      });
      (parentInRMNode.children as RenderModelContentBlock[]).push(rMPara);

      return {
        ...state,
        heightPx: heightPx + rMPara.heightPx,
      };
    }
    case 'table': {
      /**
       * TODO - Add tests for this.
       * @url https://arity.atlassian.net/browse/AL-1855
       */
      const rMTable = getOrCreateCachedRMNode(editor, {
        cMNodeEntry: [cMChild, cMChildPath],
        containerWidthPx,
        isInHeaderFooter: true,
        pageNum,
      });
      (parentInRMNode.children as RenderModelContentBlock[]).push(rMTable);

      return {
        ...state,
        heightPx: heightPx + rMTable.heightPx,
      };
    }
    case 'dynamic-block': {
      const dynamicBlock = getOrCreateCachedRMNode(editor, {
        cMNodeEntry: [cMChild, cMChildPath],
        containerWidthPx,
        isInHeaderFooter: true,
        pageNum,
      });
      (parentInRMNode.children as RenderModelContentBlock[]).push(dynamicBlock);

      return {
        ...state,
        heightPx: heightPx + dynamicBlock.heightPx,
      };
    }
    case 'instructionField': {
      console.warn('instructionField not supported in header/footer', cMChild);
      return state;
    }
    default: {
      assertNeverSafely(cMChild);
      return state;
    }
  }
}

export function buildHeaderOrFooter(
  entry: NodeEntry<HeaderElement>,
  editor: Editor,
  pageNum: number
): RenderModelHeader;
export function buildHeaderOrFooter(
  entry: NodeEntry<FooterElement>,
  editor: Editor,
  pageNum: number
): RenderModelFooter;
export function buildHeaderOrFooter(
  entry: NodeEntry<HeaderElement> | NodeEntry<FooterElement>,
  editor: Editor,
  pageNum: number
): RenderModelHeader | RenderModelFooter {
  const [headerOrFooter, headerOrFooterPath] = entry;
  const initialState: ReduceCMNodeState = {
    cMParentPath: headerOrFooterPath,
    editor,
    heightPx: 0,
    pageNum,
    rMNode: {
      ...headerOrFooter,
      children: [],
      // Stub height and override/set correct heightPx when returning to satisfy Slate types.
      heightPx: 0,
      relativePath: Queries.getPathRelativeToParent(headerOrFooterPath),
    },
    rMParentPath: [],
  };
  const { heightPx, rMNode } = headerOrFooter.children.reduce(reduceCMChild, initialState);

  return { ...rMNode, heightPx };
}
